import React, { useState } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';

// Components
import Navbar from './components/Navbar';
import HomeHero from './components/home/HomeHero';
import HomeAreas from './components/home/HomeAreas';
import HomeGraph from './components/home/HomeGraph';
import HomePreview from './components/home/HomePreview';
import HomeCta from './components/home/HomeCta';
import Footer from './components/Footer';

// Pages
import PaymentSuccess from './pages/PaymentSuccess';
import SubscribePage from './pages/SubscribePage';
import AdminDashboard from './pages/AdminDashboard';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AckPage from './pages/AckPage';
import ArrivalPage from './pages/ArrivalPage';
import MilanoPreviewPage from './pages/MilanoPreviewPage';
import TravelerApp from './traveler/TravelerApp';
import SignReleasePage from './pages/SignReleasePage';
import Dashboard from './pages/Dashboard';
import { PortalAuthProvider } from './portal/PortalAuth';
import PortalLogin from './portal/PortalLogin';
import PortalLayout from './portal/PortalLayout';
import PortalDashboard from './portal/pages/DashboardPage';
import SearchPage from './portal/pages/SearchPage';
import GuidePage from './portal/pages/GuidePage';
import PeoplePage from './portal/pages/PeoplePage';
import ReportsPage from './portal/pages/ReportsPage';
import ReportPage from './portal/pages/ReportPage';
import GraphPage from './portal/pages/GraphPage';
import MonitoringPage from './portal/pages/MonitoringPage';
import WalletPage from './portal/pages/WalletPage';
import SecurityPage from './portal/pages/SecurityPage';
import ReputationalPage from './portal/pages/ReputationalPage';
import ApiUsagePage from './portal/pages/ApiUsagePage';
import ReputationalCheckPage from './portal/pages/ReputationalCheckPage';
import AmlGraphPage from './portal/pages/AmlGraphPage';
import TravelCountryPage from './portal/pages/TravelCountryPage';
import TravelCompliancePage from './portal/pages/TravelCompliancePage';
import TravelTripPage from './portal/pages/TravelTripPage';
import OsintPage from './portal/pages/OsintPage';
import TravelSecurityPage from './portal/pages/TravelSecurityPage';
import EsteroPage from './portal/pages/EsteroPage';
import ImmobiliPage from './portal/pages/ImmobiliPage';
import PeopleReportPage from './portal/pages/PeopleReportPage';
import DossierPage from './portal/pages/DossierPage';
import TeamPage from './portal/pages/TeamPage';
import WhistleblowingPage from './portal/pages/WhistleblowingPage';
import WbPublicPage from './pages/WbPublicPage';
import RequestServicePage from './portal/pages/RequestServicePage';
import { WB_HOST } from './pages/WbPublicPage';
import SecurityChecksPage from './portal/pages/SecurityChecksPage';
import CyberLegalPage from './portal/pages/CyberLegalPage';
import AreaButtonsPreviewPage from './portal/pages/AreaButtonsPreviewPage';
import InvitePage from './pages/InvitePage';

// Landing Page Component
function LandingPage() {
  return (
    <div className="min-h-screen bg-[#050505] overflow-x-hidden">
      <Navbar onRegisterClick={() => {}} />
      
      <main>
        <HomeHero />
        <HomeAreas />
        <HomeGraph />
        <HomePreview />
        <HomeCta />
      </main>

      <Footer />
    </div>
  );
}

function App() {
  if (WB_HOST) return (
    <BrowserRouter>
      <Routes>
        <Route path="/:slug" element={<WbPublicPage />} />
        <Route path="/:slug/stato" element={<WbPublicPage status />} />
        <Route path="*" element={<WbPublicPage />} />
      </Routes>
    </BrowserRouter>
  );
  return (
    <LanguageProvider>
      <AuthProvider>
        <PortalAuthProvider>
        <BrowserRouter>
          <Toaster 
            position="bottom-right"
            visibleToasts={2}
            duration={3000}
            toastOptions={{
              style: {
                background: '#0A0A0A',
                border: '1px solid #262626',
                color: '#E5E5E5',
              },
            }}
          />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Navigate to="/portal/login" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/conferma-lettura/:token" element={<AckPage />} />
            <Route path="/arrivo/:token" element={<ArrivalPage />} />
            <Route path="/segnala/:slug" element={<WbPublicPage />} />
            <Route path="/segnala/:slug/stato" element={<WbPublicPage status />} />
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/pricing" element={<LandingPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/milano-preview" element={<MilanoPreviewPage />} />
            <Route path="/viaggiatore" element={<TravelerApp />} />
            <Route path="/firma-manleva/:token" element={<SignReleasePage />} />
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/invito/:token" element={<InvitePage />} />
            <Route path="/portal" element={<PortalLayout />}>
              <Route index element={<PortalDashboard />} />
              <Route path="ricerca" element={<SearchPage />} />
              <Route path="persone" element={<PeoplePage />} />
              <Route path="report" element={<ReportsPage />} />
              <Route path="report/persona/:id" element={<PeopleReportPage />} />
              <Route path="report/:id" element={<ReportPage />} />
              <Route path="dossier" element={<DossierPage />} />
              <Route path="grafo" element={<GraphPage />} />
              <Route path="monitoraggio" element={<MonitoringPage />} />
              <Route path="guida" element={<GuidePage />} />
              <Route path="reputational" element={<ReputationalPage />} />
              <Route path="reputational/:id" element={<ReputationalCheckPage />} />
              <Route path="reputational/:id/grafo" element={<AmlGraphPage />} />
              <Route path="osint" element={<OsintPage />} />
              <Route path="travel-security" element={<TravelSecurityPage />} />
              <Route path="travel-security/compliance" element={<TravelCompliancePage />} />
              <Route path="travel-security/trasferte/:id" element={<TravelTripPage />} />
              <Route path="travel-security/:iso3" element={<TravelCountryPage />} />
              <Route path="estero" element={<EsteroPage />} />
              <Route path="immobili" element={<ImmobiliPage />} />
              <Route path="negativita" element={<Navigate to="/portal/persone?tab=negativita" replace />} />
              <Route path="whistleblowing" element={<WhistleblowingPage />} />
              <Route path="kit-documenti" element={<Navigate to="/portal/whistleblowing?tab=kit" replace />} />
              <Route path="richieste/:service" element={<RequestServicePage />} />
              <Route path="verifiche-sicurezza" element={<SecurityChecksPage />} />
              <Route path="cyber-security" element={<CyberLegalPage />} />
              <Route path="anteprima-bottoni" element={<AreaButtonsPreviewPage />} />
              <Route path="utenti" element={<TeamPage />} />
              <Route path="api-aml" element={<ApiUsagePage />} />
              <Route path="plafond" element={<WalletPage />} />
              <Route path="sicurezza" element={<SecurityPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </PortalAuthProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
